/**
 * Pure wizard → scenario mapping (extracted from ContentCreationWizard so it can
 * be unit-tested without rendering the client component).
 *
 * The wizard's mode + output toggles determine which SC-xx scenario — and hence
 * which workflow DAG — is created. Keeping this a pure function lets us assert
 * that every reachable input combination maps to the correct scenario.
 */

import type { WizardScenario } from '../types/enums';
import { locales, defaultLocale, type Locale } from '@/shared/i18n';

export type WizardMode = 'new' | 'extend' | 'translate' | 'remix';
export type CreationMode = 'generate' | 'from-text';

/**
 * Default language for a NEW story. A source story's language wins (extend/translate
 * must stay in its language); otherwise it follows the INTERFACE locale the user is
 * actually looking at — not their stored preferredLocale, which is why a story opened
 * in the English UI must default to English, not Russian.
 */
export function resolveInitialStoryLanguage(
  preselectedStoryLanguage: string | undefined,
  uiLocale: string | undefined,
  fallbackLocale?: string | undefined,
): Locale {
  const ok = (v: string | undefined): v is Locale =>
    !!v && (locales as readonly string[]).includes(v);
  if (ok(preselectedStoryLanguage)) return preselectedStoryLanguage;
  if (ok(uiLocale)) return uiLocale;
  if (ok(fallbackLocale)) return fallbackLocale;
  return defaultLocale;
}

/**
 * Map an active (non-translate) wizard mode + creation mode + output toggles to a
 * scenario. `translate` is handled by {@link resolveScenario}, which short-circuits
 * to SC-07 before this runs.
 */
export function deriveScenario(
  wizardMode: WizardMode,
  creationMode: CreationMode,
  wantAudio: boolean,
  wantIllustrations: boolean,
): WizardScenario {
  if (wizardMode === 'extend') {
    if (wantAudio && wantIllustrations) return 'SC-11';
    if (wantAudio) return 'SC-05';
    if (wantIllustrations) return 'SC-06';
    return 'SC-05'; // fallback — extend always needs at least audio or book
  }
  if (creationMode === 'from-text') {
    // from-text-ness rides in the task params (fromTextMode/basisStoryId), so the
    // scenario only selects the DAG. SC-04's DAG (story+audio+book) is exactly what
    // from-text-full needs — without this, asking for BOTH silently dropped the
    // book (SC-09 = story+audio only) and no illustrations were ever created.
    if (wantAudio && wantIllustrations) return 'SC-04';
    if (wantAudio) return 'SC-09';
    if (wantIllustrations) return 'SC-10';
    return 'SC-08';
  }
  // generate
  if (wantAudio && wantIllustrations) return 'SC-04';
  if (wantAudio) return 'SC-02';
  if (wantIllustrations) return 'SC-03';
  return 'SC-01';
}

/**
 * Full scenario resolution incl. the translate short-circuit and the null (no
 * mode chosen yet) case — mirrors the wizard's `scenario` memo.
 */
export function resolveScenario(
  wizardMode: WizardMode | null,
  creationMode: CreationMode,
  wantAudio: boolean,
  wantIllustrations: boolean,
): WizardScenario | null {
  if (wizardMode === 'translate') return 'SC-07';
  if (!wizardMode) return null;
  return deriveScenario(wizardMode, creationMode, wantAudio, wantIllustrations);
}
